import { OpenProjectRoster } from "./openproject-roster.js";
import { useEffect, useState } from "react";
import { api, type Json, type Session } from "../api.js";
import {
  failure,
  identifier,
  Notice,
  ObjectTable,
  ReasonAction,
  useData,
} from "../shared/ui.js";

export function OpenProjectPage({ session }: { session: Session }) {
  const availability = useData(() => api.openProjectAvailability(), []);
  const available = availability.value?.available === true;
  const config = useData(
    () => (available ? api.openProject() : Promise.resolve<Json>({})),
    [available],
  );
  const sync = useData(
    () => (available ? api.syncStatus() : Promise.resolve<Json>({})),
    [available],
  );
  const history = useData(
    () => (available ? api.openProjectHistory() : Promise.resolve<Json>({})),
    [available],
  );
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!available) return;
    setEnabled(config.value?.["mode"] === "enabled");
  }, [available, config.value]);

  const meta = (reason: string): Json => ({
    reason,
    expectedAuthorizationRevision: session.principal.authorizationRevision,
    idempotencyKey: identifier(),
  });

  if (availability.error !== undefined)
    return (
      <section
        className="control-page openproject-page unified-operations-control-page"
        data-page-layout="fiori-floorplan-openproject"
      >
        <Notice error>
          {failure(availability.error)}{" "}
          <button type="button" onClick={availability.refresh}>
            Retry
          </button>
        </Notice>
      </section>
    );

  if (availability.value === undefined)
    return (
      <section
        className="control-page openproject-page unified-operations-control-page"
        data-page-layout="fiori-floorplan-openproject"
      >
        <Notice>Checking whether OpenProject sync is available…</Notice>
      </section>
    );

  if (!available)
    return (
      <section
        className="control-page openproject-page unified-operations-control-page"
        data-page-layout="fiori-floorplan-openproject"
      >
        <section
          className="page-guide state-panel"
          aria-labelledby="openproject-unavailable-title"
        >
          <div>
            <span className="status-badge" data-tone="neutral">
              Not connected
            </span>
            <h2 id="openproject-unavailable-title">Connection unavailable</h2>
            <p>
              Connect OpenProject to import people and timesheets into PayDay.
              PayDay does not write back to OpenProject.
            </p>
          </div>
          <p>
            Configure the connection for this deployment to enable Pull and new
            timesheet locks. Saved payroll evidence remains available.
          </p>
        </section>
      </section>
    );

  const messages = [config, sync, history]
    .flatMap((item) => (item.error === undefined ? [] : [failure(item.error)]))
    .filter((message, index, all) => all.indexOf(message) === index);

  return (
    <section
      className="control-page openproject-page unified-operations-control-page"
      data-page-layout="fiori-floorplan-openproject"
    >
      <header className="section-heading">
        <h2 id="openproject-guide-title">OpenProject connection</h2>
        <BooleanStatus value={config.value?.["mode"] === "enabled"} />
      </header>
      <div className="openproject-command-grid">
        <section
          className="openproject-sync-action"
          aria-labelledby="manual-sync-title"
        >
          <h3 id="manual-sync-title">Refresh source data</h3>
          <ReasonAction
            label="Sync now"
            compact
            disabled={config.value?.["mode"] !== "enabled"}
            submit={(reason) =>
              api.sync(meta(reason)).then(() => {
                sync.refresh();
                history.refresh();
                config.refresh();
              })
            }
          />
        </section>
        <section
          className="connection-settings"
          aria-labelledby="connection-settings-title"
        >
          <h3 id="connection-settings-title">Connection settings</h3>
          <ReasonAction
            label="Save synchronization settings"
            compact
            submit={(reason) =>
              api
                .configure({
                  ...meta(reason),
                  expectedConfigurationRevision: Number(
                    config.value?.["revision"] ?? 0,
                  ),
                  enabled,
                })
                .then(config.refresh)
            }
          >
            <label className="choice-row">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => {
                  setEnabled(event.target.checked);
                }}
              />
              <span>
                <strong>Enable OpenProject connection</strong>
              </span>
            </label>
          </ReasonAction>
        </section>
      </div>

      <OpenProjectRoster session={session} />
      <ObjectTable
        title="Recent synchronization runs"
        description="Use this history to confirm whether an import completed successfully."
        rows={
          Array.isArray(history.value?.["entries"])
            ? (history.value["entries"] as Json[])
            : []
        }
        columns={["startedAt", "completedAt", "status", "runId"]}
        columnLabels={{
          startedAt: "Started",
          completedAt: "Completed",
          runId: "Run ID",
        }}
        emptyMessage="No synchronization runs have been recorded yet. Run a manual sync to test the connection."
      />

      {messages.map((message) => (
        <Notice error key={message}>
          {message}
        </Notice>
      ))}
    </section>
  );
}

function BooleanStatus({ value }: { value: boolean }) {
  return (
    <span className="status-badge" data-tone={value ? "positive" : "neutral"}>
      {value ? "Yes" : "No"}
    </span>
  );
}
